import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import LeaderboardSlider from '../components/LeaderboardSlider';
import ScrollMemoryWrapper from '../components/ScrollMemoryWrapper';
import { useSectionScrollMemory } from '../hooks/useSectionScrollMemory';
import { useSEO } from '../hooks/useSEO';
import { useTranslation } from 'react-i18next';
import { authAPI, divisionsAPI } from '../services/api';
import { HOME_VIDEOS } from '../utils/videoCdnUrl';

const HomeContainer = styled.div`
  min-height: ${(p) => (p.$embedded ? 'auto' : '100vh')};
  background-color: #ffffff;
  color: #1f2937;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
`;

const HeroSection = styled.section`
  padding: ${(p) => (p.$embedded ? '48px 0 64px' : '120px 0 80px')};
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 50%, #ffffff 100%);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, rgba(213, 43, 30, 0.15) 0%, transparent 30%, rgba(4, 54, 99, 0.15) 70%, rgba(213, 43, 30, 0.1) 100%);
    pointer-events: none;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 20px;
    right: 20px;
    width: 200px;
    height: 200px;
    background: radial-gradient(circle, rgba(213, 43, 30, 0.2) 0%, transparent 70%);
    border-radius: 50%;
    pointer-events: none;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    text-align: center;
    gap: 40px;
  }
`;

const HeroLeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 768px) {
    display: contents; /* This makes the children participate in the parent's flex layout */
  }
`;


const HeroKicker = styled.p`
  font-size: 1.15rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.35rem 0;
  line-height: 1.35;
  opacity: 0.96;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
`;

const HeroText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (max-width: 768px) {
    order: 1;
  }
  
  h1 {
    font-size: 3.5rem;
    font-weight: 800;
    color: #ffffff;
    margin: 0;
    line-height: 1.1;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    
    @media (max-width: 768px) {
      font-size: 2.5rem;
    }
    
    @media (max-width: 480px) {
      font-size: 2rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    color: #ffffff;
    margin: 0;
    line-height: 1.6;
    opacity: 0.95;
    
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
    
    @media (max-width: 480px) {
      font-size: 1rem;
    }
  }
`;

const HeroActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (max-width: 768px) {
    order: 4;
  }
`;

const HeroVideo = styled.div`
  position: relative;
  
  video {
    width: 100%;
    height: auto;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    object-fit: cover;
    /* Completely disable any hover effects and controls */
    pointer-events: none;
  }
  
  /* Re-enable pointer events for the container to handle clicks */
  &:hover video {
    pointer-events: none;
  }
  
  img {
    width: 100%;
    height: auto;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    order: 3;
  }
  
  .logo-section {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 2rem;
    
    img {
      width: 180px;
      height: 180px;
      border-radius: 12px;
      object-fit: contain;
    }
    
    .logo-text {
      h1 {
        color: #ffffff;
        margin: 0 0 0.5rem 0;
        font-size: 2.5rem;
        font-weight: 700;
        line-height: 1.2;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
      }
      
      .subtitle {
        color: #ffffff;
        font-size: 1.1rem;
        font-weight: 600;
        margin: 0;
        opacity: 0.9;
      }
    }
  }
  
  h1 {
    color: #ffffff;
    margin-bottom: 1.5rem;
    font-size: 2.5rem;
    font-weight: 700;
    line-height: 1.2;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
    
    @media (max-width: 768px) {
      font-size: 2rem;
      margin-bottom: 1rem;
    }
  }
  
  p {
    font-size: 1.2rem;
    margin-bottom: 2rem;
    color: #ffffff;
    line-height: 1.6;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.4);
    opacity: 0.95;
    
    @media (max-width: 768px) {
      font-size: 1rem;
      margin-bottom: 1.5rem;
      line-height: 1.5;
    }
  }
`;

const HeroButtons = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 3rem;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 12px;
    margin-bottom: 2rem;
  }
`;

const ButtonStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 12px;
  }
`;

const Button = styled(Link).withConfig({
  shouldForwardProp: (prop) => !['primary', 'secondary', 'large'].includes(prop),
})`
  display: inline-block;
  padding: ${props => props.$large ? '16px 32px' : '12px 24px'};
  border: none;
  border-radius: 8px;
  font-size: ${props => props.$large ? '0.95rem' : '1rem'};
  font-weight: 500;
  text-decoration: none;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-family: inherit;
  
  ${props => props.$primary ? `
    background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
    color: white;
    border: 2px solid #043663;
    
    &:hover {
      background: linear-gradient(135deg, #032a4a 0%, #043663 100%);
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(4, 54, 99, 0.3);
    }
  ` : props.$secondary ? `
    background: linear-gradient(135deg, #d52b1e 0%, #e6392a 100%);
    color: white;
    border: 2px solid #d52b1e;
    
    &:hover {
      background: linear-gradient(135deg, #b8241a 0%, #d52b1e 100%);
      transform: translateY(-2px);
      box-shadow: 0 10px 15px -3px rgba(213, 43, 30, 0.3);
    }
  ` : `
    background-color: transparent;
    color: #ffffff;
    border: 2px solid #ffffff;
    
    &:hover {
      background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
      color: white;
      transform: translateY(-2px);
    }
  `}
`;

const HeroStats = styled.div`
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (max-width: 768px) {
    gap: 16px;
  }
  
  @media (max-width: 480px) {
    gap: 12px;
  }
`;

const Stat = styled.div`
  text-align: center;
  position: relative;
  
  .stat-number {
    display: block;
    font-size: 1.8rem;
    font-weight: 700;
    color: ${props => props.$accent ? '#d52b1e' : '#ffffff'};
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    
    @media (max-width: 768px) {
      font-size: 1.4rem;
    }
  }
  
  .stat-label {
    font-size: 0.8rem;
    color: #ffffff;
    opacity: 0.8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    line-height: 1.2;
    max-width: 120px;
    
    @media (max-width: 768px) {
      font-size: 0.7rem;
      max-width: 100px;
    }
  }
`;

const HeroImage = styled.div`
  video {
    width: 100%;
    height: auto;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    object-fit: cover;
  }
  
  img {
    width: 100%;
    height: auto;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  }
`;

const Section = styled.section`
  padding: 80px 0;
  
  h2 {
    text-align: center;
    margin-bottom: 3rem;
    color: #1f2937;
    font-size: 2rem;
    font-weight: 600;
  }
`;

const HowItWorksSection = styled(Section)`
  background-color: #f8fafc;
`;

const FounderVideoSection = styled(Section)`
  background-color: #ffffff;
  padding: 60px 0;
  position: relative;
  cursor: pointer;
  
  video {
    width: 100%;
    height: auto;
    border-radius: 16px;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    object-fit: contain;
    display: block;
    pointer-events: none;
    max-width: 100%;
  }
  
  @media (max-width: 768px) {
    padding: 40px 0;
    
    video {
      border-radius: 12px;
    }
  }
`;

const StepsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 32px;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  padding: 32px 24px;
  background-color: #ffffff;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  height: 100%;
  
  &:hover {
    transform: translateY(-8px);
  }
  
  .step-number {
    width: 60px;
    height: 60px;
    background-color: #043663;
    color: white;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 auto 24px;
    flex-shrink: 0;
  }
  
  h3 {
    color: #1f2937;
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 600;
    min-height: 3.6rem;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 1.2;
  }
  
  p {
    color: #6b7280;
    line-height: 1.6;
    flex-grow: 1;
    display: flex;
    align-items: flex-start;
  }
`;

const SeasonsSection = styled(Section)`
  background-color: #ffffff;
`;

const SeasonInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 32px;
`;

const SeasonCard = styled.div`
  background-color: #ffffff;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  text-align: center;
  border: 2px solid #e5e7eb;
  
  h3 {
    color: #043663;
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  .deadline, .start-date, .games-count {
    font-size: 1.5rem;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 1rem;
  }
  
  .status {
    display: inline-block;
    padding: 8px 16px;
    background-color: #f8fafc;
    color: #6b7280;
    border-radius: 20px;
    font-size: 0.9rem;
    font-weight: 500;
    
    &.active {
      background-color: #16a34a;
      color: white;
    }
  }
`;

// eslint-disable-next-line no-unused-vars
const LeaderboardSection = styled(Section)`
  background-color: #f8fafc;
`;

// eslint-disable-next-line no-unused-vars
const LeaderboardTable = styled.div`
  background-color: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

// eslint-disable-next-line no-unused-vars
const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 80px 80px 80px;
  padding: 16px 12px;
  background-color: #043663;
  color: white;
  font-weight: 600;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 50px 1fr 60px 60px 60px;
    padding: 12px 8px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 40px 1fr 50px 50px 50px;
    padding: 10px 6px;
    font-size: 0.75rem;
  }
`;

// eslint-disable-next-line no-unused-vars
const TableRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 80px 80px 80px;
  padding: 16px 12px;
  border-bottom: 1px solid #e5e7eb;
  align-items: center;
  font-size: 0.9rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 50px 1fr 60px 60px 60px;
    padding: 12px 8px;
    font-size: 0.8rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 40px 1fr 50px 50px 50px;
    padding: 10px 6px;
    font-size: 0.75rem;
  }
  
  &:last-child {
    border-bottom: none;
  }
  
  &:nth-child(even) {
    background-color: #f8fafc;
  }
  
  .rank {
    font-weight: 700;
    color: #043663;
    text-align: center;
  }
  
  .team {
    font-weight: 500;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding-right: 8px;
  }
  
  .points {
    font-weight: 600;
    color: #16a34a;
    text-align: center;
  }
  
  .games, .record {
    text-align: center;
    font-weight: 500;
  }
`;

// eslint-disable-next-line no-unused-vars
const LeaderboardActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-top: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;


const HighlightsSection = styled(Section)`
  background-color: #ffffff;
`;

const HighlightsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 32px;
`;

const HighlightCard = styled.div`
  text-align: center;
  padding: 32px 24px;
  background-color: #f8fafc;
  border-radius: 16px;
  border: 2px solid #e5e7eb;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-8px);
  }
  
  .highlight-icon {
    width: 80px;
    height: 80px;
    background-color: #043663;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
    color: white;
    font-size: 2rem;
  }
  
  h3 {
    color: #1f2937;
    margin-bottom: 1rem;
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  .highlight-number {
    font-size: 3rem;
    font-weight: 700;
    color: #043663;
    margin: 16px 0 8px;
  }
  
  .highlight-team {
    font-size: 1.5rem;
    font-weight: 600;
    color: #043663;
    margin: 16px 0 8px;
  }
  
  .highlight-label {
    font-size: 0.9rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const RecentMatchesSection = styled(Section)`
  background-color: #f8fafc;
`;

const MatchesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 2rem;
`;

const MatchResult = styled.div`
  background-color: #ffffff;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 2px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 12px;
  
  .team-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
  }
  
  .team {
    font-weight: 600;
    color: #1f2937;
    font-size: 1.1rem;
    flex: 1;
    text-align: left;
    min-width: 0;
    margin-right: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
    overflow: hidden;
  }
  
  .team > span:first-child {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
    min-width: 0;
  }
  
  .result-badge {
    font-size: 0.75rem;
    font-weight: 600;
    padding: 2px 8px;
    border-radius: 12px;
    white-space: nowrap;
    flex-shrink: 0;
    
    &.won {
      background-color: #16a34a;
      color: white;
    }
    
    &.lost {
      background-color: #dc2626;
      color: white;
    }
  }
  
  .score-center {
    font-weight: 700;
    color: #043663;
    font-size: 1.1rem;
    padding: 8px 16px;
    background-color: #f0f9ff;
    border-radius: 8px;
    text-align: center;
    white-space: nowrap;
    margin: 8px 0;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .vs {
    font-size: 0.85rem;
    color: #9ca3af;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 1px;
    text-align: center;
    margin: 4px 0;
  }
  
  .match-date {
    font-size: 0.85rem;
    color: #6b7280;
    margin-top: 8px;
    padding-top: 16px;
    border-top: 1px solid #e5e7eb;
    width: 100%;
    text-align: center;
  }
`;

const MatchesActions = styled.div`
  text-align: center;
`;

const CTASection = styled(Section)`
  background: linear-gradient(135deg, #043663 0%, #032a4a 100%);
  color: white;
  text-align: center;
  
  h2 {
    color: white;
    margin-bottom: 1.5rem;
  }
  
  p {
    color: #e0e7ff;
    font-size: 1.2rem;
    margin-bottom: 2rem;
    max-width: 600px;
    margin-left: auto;
    margin-right: auto;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 3rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const CTAStats = styled.div`
  display: flex;
  gap: 48px;
  justify-content: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const CTAStat = styled.div`
  text-align: center;
  
  .cta-number {
    display: block;
    font-size: 2.5rem;
    font-weight: 700;
    color: #f59e0b;
  }
  
  .cta-label {
    font-size: 0.9rem;
    color: #e0e7ff;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const ContactSection = styled(Section)`
  background-color: #f8fafc;
`;

// eslint-disable-next-line no-unused-vars
const ContactContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: start;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

// eslint-disable-next-line no-unused-vars
const ContactInfo = styled.div`
  h3 {
    color: #043663;
    margin-bottom: 24px;
    font-size: 1.5rem;
    font-weight: 600;
  }
  
  p {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    color: #1f2937;
    font-size: 1rem;
  }
`;

const ContactForm = styled.div`
  background-color: #ffffff;
  padding: 32px;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 500px;
  
  .form-group {
    margin-bottom: 24px;
  }
  
  input, textarea {
    width: 100%;
    padding: 12px 16px;
    border: 2px solid #e5e7eb;
    border-radius: 8px;
    font-size: 1rem;
    font-family: inherit;
    transition: border-color 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #043663;
    }
  }
  
  textarea {
    resize: vertical;
    min-height: 120px;
  }
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 12px 24px;
  background: linear-gradient(135deg, #043663 0%, #032a4a 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(4, 54, 99, 0.3);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const PartnerClubsSection = styled(Section)`
  background-image: url('/images/clubs/clubs.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  position: relative;
  min-height: 500px;
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    z-index: 1;
  }
`;

const PartnerClubsContent = styled.div`
  position: relative;
  z-index: 2;
  text-align: center;
  color: white;
  max-width: 800px;
  margin: 0 auto;
  
  h2 {
    color: white;
    margin-bottom: 2rem;
    font-size: 2.5rem;
    font-weight: 700;
    text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  }
  
  p {
    font-size: 1.25rem;
    line-height: 1.6;
    margin: 0;
    text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.5);
  }
`;

// Helper function to format scores with bold winning numbers for recent matches
// Combines team1 and team2 scores into "4-6 ; 6-4 ; 10-8" format with bold winners and dull losers
const formatMatchScoresWithBold = (team1Scores, team2Scores) => {
  if (!team1Scores || !team2Scores || team1Scores.length === 0 || team2Scores.length === 0) {
    return '-';
  }
  
  const maxLength = Math.max(team1Scores.length, team2Scores.length);
  const sets = [];
  
  for (let i = 0; i < maxLength; i++) {
    const score1 = team1Scores[i];
    const score2 = team2Scores[i];
    
    // Only show set if both scores are valid (not null/undefined) AND at least one is > 0
    // This prevents showing "0-0" for sets that weren't played (like 3rd set tiebreaker)
    if (score1 !== undefined && score2 !== undefined && score1 !== null && score2 !== null &&
        (score1 > 0 || score2 > 0)) {
      const isFirstWinner = score1 > score2;
      
      sets.push(
        <React.Fragment key={i}>
          {i > 0 && ' ; '}
          {isFirstWinner ? (
            <>
              <strong>{score1}</strong>-<span style={{ opacity: 0.5, color: '#6b7280' }}>{score2}</span>
            </>
          ) : score2 > score1 ? (
            <>
              <span style={{ opacity: 0.5, color: '#6b7280' }}>{score1}</span>-<strong>{score2}</strong>
            </>
          ) : (
            <>
              {score1}-{score2}
            </>
          )}
        </React.Fragment>
      );
    }
  }
  
  return sets.length > 0 ? sets : '-';
};

/** Calendar date from API (YYYY-MM-DD) as DD.MM.YY — avoids UTC/local shifting from Date parsing */
const formatSeasonStartDdMmYy = (value) => {
  if (value == null || value === '') return '';
  const part = String(value).split('T')[0];
  const m = part.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  const [, y, mo, da] = m;
  return `${da}.${mo}.${y.slice(-2)}`;
};

/**
 * @param {{ embedded?: boolean }} props
 * When embedded (mobile app /app-info), hide site chrome CTAs; Flutter owns nav.
 */
const HomePage = ({ embedded = false }) => {
  const [videoMuted, setVideoMuted] = useState(true); // Start muted for production compatibility
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [webmSupported, setWebmSupported] = useState(true); // Check if WebM is supported
  const { t } = useTranslation();
  const [seasonData, setSeasonData] = useState(null);
  const [seasonLoading, setSeasonLoading] = useState(true);
  const founderVideoRef = React.useRef(null);
  const [founderVideoMuted, setFounderVideoMuted] = useState(true); // Start muted for autoplay compatibility
  
  // Section-specific scroll memory for leaderboard
  const { scrollContainerRef: leaderboardRef } = useSectionScrollMemory('leaderboard');
  
  // Fetch season data
  useEffect(() => {
    const fetchSeasonData = async () => {
      try {
        setSeasonLoading(true);
        const response = await authAPI.getSeasonStatus();
        if (response.success) {
          setSeasonData(response.data);
        }
      } catch (error) {
        console.error('Error fetching season data:', error);
      } finally {
        setSeasonLoading(false);
      }
    };

    fetchSeasonData();
  }, []);

  // Check WebM support
  useEffect(() => {
    const checkWebMSupport = () => {
      const video = document.createElement('video');
      const webmSupported = video.canPlayType('video/webm; codecs="vp8, vorbis"') !== '';
      setWebmSupported(webmSupported);
      
      // Log for debugging
      if (!webmSupported) {
        console.log('WebM not supported, will show fallback content');
      }
    };
    
    checkWebMSupport();
  }, []);

  // Mobile-optimized video autoplay
  useEffect(() => {
    const video = document.querySelector('video');
    if (!video) return;

    let autoplayAttempted = false;

    // Set up event listeners
    const handlePlay = () => {
      setVideoPlaying(true);
      console.log('✅ Video started playing');
    };
    
    const handlePause = () => {
      setVideoPlaying(false);
      console.log('⏸️ Video paused');
    };

    const handleError = (e) => {
      console.error('❌ Video error occurred:', e);
      setVideoPlaying(false);
      // Don't show fallback image immediately - let the video try to recover
    };

    const handleLoadStart = () => {
      console.log('🔄 Video load started');
    };

    const handleLoadedData = () => {
      console.log('✅ Video data loaded successfully');
    };

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('error', handleError);
    video.addEventListener('loadstart', handleLoadStart);
    video.addEventListener('loadeddata', handleLoadedData);

    // Production-safe autoplay strategy
    const attemptProductionAutoplay = async () => {
      if (autoplayAttempted) return;
      autoplayAttempted = true;

      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      console.log(`🎬 Attempting ${isMobile ? 'mobile' : 'desktop'} autoplay on ${isLocalhost ? 'localhost' : 'production'}...`);
      
      // Ensure video is ready and not errored
      if (video.error) {
        console.error('❌ Video has error:', video.error);
        return;
      }
      
      // Check if video has enough data to play
      if (video.readyState < 3) {
        console.log('⚠️ Video not ready enough, waiting...');
        video.addEventListener('canplaythrough', attemptProductionAutoplay, { once: true });
        return;
      }
      
      try {
        // Always start muted for production compatibility
        video.muted = true;
        setVideoMuted(true);
        
        // For desktop, wait a bit longer to ensure video is fully ready
        // On production, wait even longer due to network latency
        const baseWaitTime = isMobile ? 100 : 200;
        const productionWaitTime = isLocalhost ? 0 : 300;
        const waitTime = baseWaitTime + productionWaitTime;
        
        if (waitTime > 0) {
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
        
        // Try to play muted first
        const playPromise = video.play();
        if (playPromise !== undefined) {
          await playPromise;
        }
        
        console.log(`✅ ${isMobile ? 'Mobile' : 'Desktop'} autoplay successful (muted)`);
        
        // Sound will be enabled via the video container click/touch handlers
        console.log('🎵 Sound will be enabled when user clicks/touches the video');
        
      } catch (error) {
        console.log(`❌ ${isMobile ? 'Mobile' : 'Desktop'} autoplay failed on ${isLocalhost ? 'localhost' : 'production'}:`, error.message);
        console.log('Error details:', error);
        
        // If autoplay fails, show a play button overlay
        const showPlayButton = () => {
          const playButton = document.createElement('div');
          playButton.innerHTML = '▶️ Tap to Play';
          playButton.style.cssText = `
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: rgba(4, 54, 99, 0.9);
            color: white;
            padding: 15px 30px;
            border-radius: 8px;
            font-size: 16px;
            cursor: pointer;
            z-index: 10;
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
          `;
          
          playButton.onclick = async () => {
            try {
              video.muted = true;
              await video.play();
              playButton.remove();
              console.log('✅ Video started via play button');
            } catch (playError) {
              console.log('❌ Play button failed:', playError.message);
            }
          };
          
          video.parentElement.appendChild(playButton);
        };
        
        showPlayButton();
      }
    };

    // Wait for video to be ready, then attempt autoplay
    const initializeVideo = () => {
      // Check if we're on desktop or mobile
      const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      if (video.readyState >= 2) { // HAVE_CURRENT_DATA
        if (isMobile) {
          attemptProductionAutoplay();
        } else {
          // For desktop, wait a bit longer to ensure video is fully loaded
          setTimeout(attemptProductionAutoplay, 500);
        }
      } else {
        video.addEventListener('canplay', () => {
          if (isMobile) {
            attemptProductionAutoplay();
          } else {
            // For desktop, wait a bit longer
            setTimeout(attemptProductionAutoplay, 500);
          }
        }, { once: true });
        
        video.addEventListener('loadeddata', () => {
          if (isMobile) {
            attemptProductionAutoplay();
          } else {
            // For desktop, wait a bit longer
            setTimeout(attemptProductionAutoplay, 500);
          }
        }, { once: true });
      }
    };

    // Start initialization
    initializeVideo();

    // Cleanup
    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('error', handleError);
      video.removeEventListener('loadstart', handleLoadStart);
      video.removeEventListener('loadeddata', handleLoadedData);
    };
  }, []);

  // Founder video autoplay for mobile devices
  useEffect(() => {
    const founderVideo = founderVideoRef.current;
    if (!founderVideo) return;

    const attemptAutoplay = async () => {
      try {
        founderVideo.muted = true;
        setFounderVideoMuted(true);
        await founderVideo.play();
        console.log('✅ Founder video autoplay successful');
      } catch (error) {
        console.log('⚠️ Founder video autoplay failed (will rely on autoplay attribute):', error.message);
      }
    };

    // Wait for video to be ready
    if (founderVideo.readyState >= 2) {
      attemptAutoplay();
    } else {
      founderVideo.addEventListener('canplay', attemptAutoplay, { once: true });
      founderVideo.addEventListener('loadeddata', attemptAutoplay, { once: true });
    }
  }, []);


  // SEO Meta Tags
  useSEO({
    title: 'Home - Switzerland\'s First Padel League',
    description: 'Join Switzerland\'s premier padel league! 500+ teams compete across all cantons in structured divisions. Register your team, improve your skills, and compete for the Swiss Finals. Open registration now!',
    keywords: 'padel, Switzerland, league, competition, sports, teams, canton, Swiss Finals, padel tournament, padel league, helvetic padel, padel Switzerland, padel competition, register team, padel registration',
    canonical: 'https://helveticpadelseries.ch/'
  });
  
  // Simplified video state - no retry functionality

  
  // State for season highlights data
  const [seasonHighlights, setSeasonHighlights] = useState({
    seasonProgress: {
      currentWeek: 0,
      totalWeeks: 18,
      percentage: 0
    },
    activeTeams: 30,
    matchesPlayed: 0,
    topPerformer: {
      teamName: '-',
      points: 0,
      wins: 0,
      losses: 0
    }
  });

  const [isLoading, setIsLoading] = useState(true);

  // State for recent matches
  const [recentMatches, setRecentMatches] = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(true);

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Fetch season highlights from API
  useEffect(() => {
    const loadSeasonHighlights = async () => {
      setIsLoading(true);
      
      try {
        const response = await fetch('/api/season-highlights/highlights');
        const result = await response.json();
        
        if (result.success) {
          // Use real data from database
          const updatedData = {
            ...result.data,
            activeTeams: result.data.activeTeams || 0,
            matchProgress: result.data.matchProgress || {
              currentRound: 0,
              completedRounds: 0,
              totalRounds: 6,
              percentage: 0
            },
            topPerformer: {
              teamName: result.data.topPerformer?.teamName || '-',
              points: result.data.topPerformer?.points || 0,
              wins: result.data.topPerformer?.wins || 0,
              losses: result.data.topPerformer?.losses || 0
            },
            isGapPeriod: result.data.isGapPeriod || false,
            message: result.data.message || null,
            seasonInfo: result.data.seasonInfo || null
          };
          setSeasonHighlights(updatedData);
        } else {
          console.error('Failed to fetch season highlights:', result.message);
          // Keep default values on error
        }
      } catch (error) {
        console.error('Error fetching season highlights:', error);
        // Keep default values on error
      }
      
      setIsLoading(false);
    };

    loadSeasonHighlights();
  }, []);

  // Fetch recent completed matches for current season
  useEffect(() => {
    const loadRecentMatches = async () => {
      setMatchesLoading(true);
      
      try {
        // Get current season ID from season highlights data
        const seasonId = seasonHighlights?.seasonInfo?.id || null;
        
        const response = await divisionsAPI.getRecentMatches({ 
          limit: 4,
          season_id: seasonId 
        });
        
        if (response.success) {
          setRecentMatches(response.data || []);
        } else {
          console.error('Failed to fetch recent matches:', response.message);
          setRecentMatches([]);
        }
      } catch (error) {
        console.error('Error fetching recent matches:', error);
        // Keep empty array on error
        setRecentMatches([]);
      } finally {
        setMatchesLoading(false);
      }
    };

    // Only load matches if we have season data (to get season_id)
    if (seasonHighlights?.seasonInfo || !isLoading) {
      loadRecentMatches();
      
      // Refresh matches every 30 seconds to keep them updated
      const interval = setInterval(loadRecentMatches, 30000);
      return () => clearInterval(interval);
    }
  }, [seasonHighlights?.seasonInfo?.id, isLoading]);

  // Handle scroll to contact from other pages - now handled by react-scroll in Header
  // Removed old scroll logic as it's now handled by react-scroll library

  // Handle contact form submission
  const handleContactSubmit = async (e) => {
    console.log('Form submitted!', contactForm);
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      console.log('Sending request to /api/contact/submit');
      const response = await fetch('/api/contact/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contactForm),
      });

      console.log('Response received:', response);
      const data = await response.json();
      console.log('Response data:', data);

      if (data.success) {
        setSubmitMessage('Thank you for your message. Our team will get back to you shortly.');
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitMessage(data.message || 'There was an error sending your message. Please try again.');
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setSubmitMessage('There was an error sending your message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  return (
    <ScrollMemoryWrapper enableScrollMemory={true}>
      <HomeContainer $embedded={embedded}>
        {/* Hero Section */}
        <HeroSection $embedded={embedded}>
        <Container>
          <HeroContainer>
            <HeroLeftColumn>
              <HeroText>
                <HeroKicker>{t('hero.subtitle')}</HeroKicker>
                <h1>{t('hero.title')}</h1>
                <p>{t('hero.description')}</p>
              </HeroText>
              
              <HeroActions>
              {!embedded && (
              <HeroButtons>
                  <Button $secondary $large to="/register-player">{t('hero.registerButton')}</Button>
                <ButtonStack>
                    <Button $large to="/guide-to-hps">{t('hero.howLeagueWorksButton')}</Button>
                    <Button $large to="/login">{t('hero.loginButton')}</Button>
                </ButtonStack>
              </HeroButtons>
              )}
              <HeroStats>
                <Stat>
                  <span className="stat-number">1</span>
                    <span className="stat-label">{t('hero.stats.country')}</span>
                </Stat>
                <Stat>
                  <span className="stat-number">26</span>
                    <span className="stat-label">{t('hero.stats.cantons')}</span>
                </Stat>
                <Stat>
                  <span className="stat-number">3</span>
                    <span className="stat-label">{t('hero.stats.seasons')}</span>
                </Stat>
                <Stat>
                  <span className="stat-number">18</span>
                    <span className="stat-label">{t('hero.stats.games')}</span>
                </Stat>
                <Stat $accent>
                  <span className="stat-number">1</span>
                    <span className="stat-label">{t('hero.stats.champion')}</span>
                </Stat>
              </HeroStats>
              </HeroActions>
            </HeroLeftColumn>
            
            <HeroVideo 
              onTouchStart={(e) => {
                // Don't prevent default to allow normal touch behavior
                e.stopPropagation();
              }}
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Get the video element
                const video = e.currentTarget.querySelector('video');
                if (video && !video.paused) {
                  console.log('Video container touch ended, current muted state:', videoMuted);
                  
                  // Toggle sound on touch - always toggle the current state
                  const newMutedState = !video.muted;
                  video.muted = newMutedState;
                  setVideoMuted(newMutedState);
                  
                  console.log(newMutedState ? '🔇 Sound muted via touch' : '✅ Sound enabled via touch');
                } else if (video && video.paused) {
                  console.log('Video is paused, trying to play...');
                  video.play().catch(err => console.log('Play failed:', err));
                }
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                // Only handle click on desktop (when no touch events)
                if (!('ontouchstart' in window)) {
                  const video = e.currentTarget.querySelector('video');
                  if (video && !video.paused) {
                    console.log('Video container clicked (desktop), current muted state:', videoMuted);
                    
                    // Toggle sound on click - always toggle the current state
                    const newMutedState = !video.muted;
                    video.muted = newMutedState;
                    setVideoMuted(newMutedState);
                    
                    console.log(newMutedState ? '🔇 Sound muted' : '✅ Sound enabled');
                  } else if (video && video.paused) {
                    console.log('Video is paused, trying to play...');
                    video.play().catch(err => console.log('Play failed:', err));
                  }
                }
              }}
            >
              <video 
                autoPlay 
                muted={videoMuted}
                loop 
                playsInline
                preload="auto"
                controls={false}
                disablePictureInPicture={true}
                onPlay={() => setVideoPlaying(true)}
                onPause={() => setVideoPlaying(false)}
                poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDUwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI1MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjMDQzNjYzIi8+Cjx0ZXh0IHg9IjI1MCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5QYWRlbCBNYXRjaDwvdGV4dD4KPC9zdmc+"
                style={{ 
                  width: '100%', 
                  height: 'auto', 
                  minHeight: '300px',
                  display: 'block',
                  objectFit: 'cover',
                  backgroundColor: '#043663'
                }}
                // iOS Safari
                webkit-playsinline="true"
                // Android Chrome/WebView
                x5-playsinline="true"
                x5-video-player-type="h5"
                x5-video-player-fullscreen="true"
                // Firefox mobile
                moz-playsinline="true"
                // Edge mobile
                ms-playsinline="true"
              >
                {/* MP4 first for mobile compatibility - now compressed to 15MB */}
                <source
                  src={HOME_VIDEOS.heroMp4()}
                  type="video/mp4"
                />
                {/* WebM fallback for better compression */}
                <source
                  src={HOME_VIDEOS.heroWebm()}
                  type="video/webm; codecs=vp8,vorbis"
                />
                {/* Fallback message */}
                <p style={{ 
                  color: 'white', 
                  textAlign: 'center', 
                  padding: '2rem',
                  backgroundColor: '#043663'
                }}>
                  Your browser does not support the video tag.
                </p>
              </video>
              
              {/* Fallback image - only shows if video completely fails */}
              <img
                src="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDUwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI1MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjMDQzNjYzIi8+Cjx0ZXh0IHg9IjI1MCIgeT0iMjAwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjQiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5QYWRlbCBNYXRjaDwvdGV4dD4KPC9zdmc+"
                alt="Padel Match"
                style={{ 
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%', 
                  height: '100%', 
                  objectFit: 'cover',
                  display: videoPlaying ? 'none' : 'block',
                  zIndex: 1
                }}
              />
            </HeroVideo>
          </HeroContainer>
        </Container>
      </HeroSection>

      {/* How League Works */}
      <HowItWorksSection id="how-it-works">
        <Container>
          <h2>{t('howItWorks.title')}</h2>
          <StepsGrid>
            <Step>
              <div className="step-number">1</div>
              <h3>{t('howItWorks.steps.register.title')}</h3>
              <p>{t('howItWorks.steps.register.description')}</p>
            </Step>
            <Step>
              <div className="step-number">2</div>
              <h3>{t('howItWorks.steps.schedule.title')}</h3>
              <p>{t('howItWorks.steps.schedule.description')}</p>
            </Step>
            <Step>
              <div className="step-number">3</div>
              <h3>{t('howItWorks.steps.play.title')}</h3>
              <p>{t('howItWorks.steps.play.description')}</p>
            </Step>
            <Step>
              <div className="step-number">4</div>
              <h3>{t('howItWorks.steps.climb.title')}</h3>
              <p>{t('howItWorks.steps.climb.description')}</p>
            </Step>
          </StepsGrid>
        </Container>
      </HowItWorksSection>

      {/* Founder Video */}
      <FounderVideoSection
        onTouchStart={(e) => {
          // Don't prevent default to allow normal touch behavior
          e.stopPropagation();
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Get the video element
          const video = e.currentTarget.querySelector('video');
          if (video && !video.paused) {
            console.log('Founder video container touch ended, current muted state:', founderVideoMuted);
            
            // Toggle sound on touch - always toggle the current state
            const newMutedState = !video.muted;
            video.muted = newMutedState;
            setFounderVideoMuted(newMutedState);
            
            console.log(newMutedState ? '🔇 Founder video sound muted via touch' : '✅ Founder video sound enabled via touch');
          } else if (video && video.paused) {
            console.log('Founder video is paused, trying to play...');
            video.play().catch(err => console.log('Play failed:', err));
          }
        }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          
          // Only handle click on desktop (when no touch events)
          if (!('ontouchstart' in window)) {
            const video = e.currentTarget.querySelector('video');
            if (video && !video.paused) {
              console.log('Founder video container clicked (desktop), current muted state:', founderVideoMuted);
              
              // Toggle sound on click - always toggle the current state
              const newMutedState = !video.muted;
              video.muted = newMutedState;
              setFounderVideoMuted(newMutedState);
              
              console.log(newMutedState ? '🔇 Founder video sound muted' : '✅ Founder video sound enabled');
            } else if (video && video.paused) {
              console.log('Founder video is paused, trying to play...');
              video.play().catch(err => console.log('Play failed:', err));
            }
          }
        }}
      >
        <Container>
          <video 
            ref={founderVideoRef}
            autoPlay 
            muted={founderVideoMuted}
            loop 
            playsInline
            preload="auto"
            controls={false}
            disablePictureInPicture={true}
            poster="data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTkyMCIgaGVpZ2h0PSIxMDgwIiB2aWV3Qm94PSIwIDAgMTkyMCAxMDgwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTkyMCIgaGVpZ2h0PSIxMDgwIiBmaWxsPSIjMDQzNjYzIi8+Cjx0ZXh0IHg9Ijk2MCIgeT0iNTQwIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iNDgiIGZpbGw9IndoaXRlIiB0ZXh0LWFuY2hvcj0ibWlkZGxlIj5Gb3VuZGVyIFZpZGVvPC90ZXh0Pgo8L3N2Zz4="
            style={{ 
              width: '100%', 
              height: 'auto', 
              display: 'block',
              backgroundColor: '#043663'
            }}
            // iOS Safari
            webkit-playsinline="true"
            // Android Chrome/WebView
            x5-playsinline="true"
            x5-video-player-type="h5"
            x5-video-player-fullscreen="true"
            // Firefox mobile
            moz-playsinline="true"
            // Edge mobile
            ms-playsinline="true"
          >
            <source
              src={HOME_VIDEOS.founderMp4()}
              type="video/mp4"
            />
            <p style={{ 
              color: 'white', 
              textAlign: 'center', 
              padding: '2rem',
              backgroundColor: '#043663'
            }}>
              Your browser does not support the video tag.
            </p>
          </video>
        </Container>
      </FounderVideoSection>

      {/* Current Season */}
      <SeasonsSection id="seasons">
        <Container>
          <h2>{t('seasons.title')}</h2>
          <p style={{ fontSize: '0.9rem', color: '#6b7280', textAlign: 'center', marginBottom: '2rem', marginTop: '-0.5rem' }}>
            {t('seasons.subtitle')}
          </p>
          <SeasonInfo>
            <SeasonCard>
              <h3>{t('seasons.cards.registrationDeadline.title')}</h3>
              <p className="deadline">
                {t('seasons.cards.registrationDeadline.date')}
              </p>
              <Button $secondary to="/register-player">{t('seasons.cards.registrationDeadline.button')}</Button>
            </SeasonCard>
            <SeasonCard>
              <h3>{t('seasons.cards.seasonStart.title')}:</h3>
              <p className="start-date">
                {t('seasons.cards.seasonStart.date')}
              </p>
              <span className="status active" style={{ backgroundColor: '#043663', color: 'white' }}>
                {seasonData && seasonData.registrationOpen
                  ? t('seasons.cards.seasonStart.statusRegistrationOpen')
                  : seasonData && seasonData.registrationStartsIn > 0
                    ? t('seasons.cards.seasonStart.status')
                    : t('seasons.cards.seasonStart.status')}
              </span>
            </SeasonCard>
            <SeasonCard>
              <h3>{t('seasons.cards.gamesPerSeason.title')}</h3>
              <p className="games-count">{t('seasons.cards.gamesPerSeason.count')}</p>
              <span className="status" style={{ backgroundColor: '#043663', color: 'white' }}>{t('seasons.cards.gamesPerSeason.frequency')}</span>
            </SeasonCard>
          </SeasonInfo>
        </Container>
      </SeasonsSection>

      {/* Live Leaderboard Slider */}
      <div ref={leaderboardRef} id="leaderboard">
        <LeaderboardSlider />
      </div>

      {/* Highlights for the API-reported current season (e.g. Final Sprint) */}
      <HighlightsSection>
        <Container>
          <h2>
            {!seasonHighlights.isGapPeriod && seasonHighlights.seasonInfo?.name
              ? t('highlights.seasonNameTitle', { name: seasonHighlights.seasonInfo.name })
              : t('highlights.title')}
          </h2>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '4rem 0' }}>
              <h3>{t('highlights.loading')}</h3>
            </div>
          ) : seasonHighlights.isGapPeriod ? (
            <div style={{ textAlign: 'center', padding: '4rem 2rem', maxWidth: '800px', margin: '0 auto' }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>⏳</div>
              <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#1f2937' }}>
                {seasonHighlights.seasonInfo?.name || t('highlights.gapSeasonTitle')}
              </h3>
              <p style={{ fontSize: '1.1rem', color: '#6b7280', marginBottom: '2rem', lineHeight: '1.6' }}>
                {seasonHighlights.message || t('highlights.gapSeasonBody')}
              </p>
              {seasonHighlights.seasonInfo?.startDate && (
                <div style={{ 
                  display: 'inline-block', 
                  padding: '0.75rem 1.5rem', 
                  backgroundColor: '#043663', 
                  color: 'white', 
                  borderRadius: '0.5rem',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}>
                  {t('highlights.startsOn', { date: formatSeasonStartDdMmYy(seasonHighlights.seasonInfo.startDate) })}
                </div>
              )}
            </div>
          ) : (
            <HighlightsGrid>
              <HighlightCard>
                <div className="highlight-icon">🏆</div>
                <h3>{t('highlights.cards.seasonProgress.title')}</h3>
                <p>
                  {t('highlights.matchProgress', {
                    completed: seasonHighlights.matchProgress?.completedRounds || 0,
                    total: seasonHighlights.matchProgress?.totalRounds || 6,
                  })}
                </p>
                <div style={{ width: '100%', height: '8px', backgroundColor: '#e5e7eb', borderRadius: '4px', margin: '16px 0 8px', overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${seasonHighlights.matchProgress?.percentage || 0}%`, 
                    height: '100%', 
                    backgroundColor: (seasonHighlights.matchProgress?.completedRounds || 0) >= (seasonHighlights.matchProgress?.totalRounds || 6) ? '#16a34a' : '#f59e0b', 
                    borderRadius: '4px',
                    transition: 'width 0.3s ease'
                  }}></div>
                </div>
                <span className="highlight-label">{seasonHighlights.matchProgress?.percentage || 0}% {t('highlights.cards.seasonProgress.complete')}</span>
              </HighlightCard>
              <HighlightCard>
                <div className="highlight-icon">👥</div>
                <h3>{t('highlights.cards.activeTeams.title')}</h3>
                <p className="highlight-number">{seasonHighlights.activeTeams.toLocaleString()}</p>
                <span className="highlight-label">{t('highlights.cards.activeTeams.description')}</span>
              </HighlightCard>
              <HighlightCard>
                <div className="highlight-icon">📅</div>
                <h3>{t('highlights.cards.matchesPlayed.title')}</h3>
                <p className="highlight-number">{seasonHighlights.matchesPlayed.toLocaleString()}</p>
                <span className="highlight-label">{t('highlights.cards.matchesPlayed.description')}</span>
              </HighlightCard>
              <HighlightCard>
                <div className="highlight-icon">⭐</div>
                <h3>{t('highlights.cards.topPerformer.title')}</h3>
                <p className="highlight-team">{seasonHighlights.topPerformer.teamName}</p>
                <span className="highlight-label">{seasonHighlights.topPerformer.points} {t('highlights.cards.topPerformer.description')} {seasonHighlights.topPerformer.wins}-{seasonHighlights.topPerformer.losses}</span>
              </HighlightCard>
            </HighlightsGrid>
          )}
        </Container>
      </HighlightsSection>

      {/* Recent Match Results */}
      <RecentMatchesSection>
        <Container>
          <h2>
            {seasonHighlights.seasonInfo?.name && !seasonHighlights.isGapPeriod
              ? `${t('recentMatches.title')} — ${seasonHighlights.seasonInfo.name}`
              : t('recentMatches.title')}
          </h2>
          {matchesLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <p style={{ color: '#6b7280' }}>{t('recentMatches.loading')}</p>
            </div>
          ) : recentMatches.length > 0 ? (
            <>
              <MatchesGrid>
                {recentMatches.map((match) => {
                  // Format scores with bold winning numbers (shows both teams' scores combined)
                  const scoresDisplay = formatMatchScoresWithBold(match.team1Scores, match.team2Scores);
                  
                  // Determine winner from setsScore (format: "2-0" or "3-0")
                  const [team1SetsWon, team2SetsWon] = match.setsScore 
                    ? match.setsScore.split('-').map(s => parseInt(s.trim(), 10))
                    : [0, 0];
                  const team1Won = team1SetsWon > team2SetsWon;
                  const team2Won = team2SetsWon > team1SetsWon;
                  
                  return (
                    <MatchResult key={match.id}>
                      <div className="team-row">
                        <span className="team">
                          <span>{match.team1}</span>
                          {team1Won && <span className="result-badge won">{t('recentMatches.badgeWon')}</span>}
                          {team2Won && <span className="result-badge lost">{t('recentMatches.badgeLost')}</span>}
                        </span>
                      </div>
                      <div className="score-center">
                        {scoresDisplay}
                      </div>
                      <div className="team-row">
                        <span className="team">
                          <span>{match.team2}</span>
                          {team2Won && <span className="result-badge won">{t('recentMatches.badgeWon')}</span>}
                          {team1Won && <span className="result-badge lost">{t('recentMatches.badgeLost')}</span>}
                        </span>
                      </div>
                      <span className="match-date">{match.formattedDate}</span>
                    </MatchResult>
                  );
                })}
              </MatchesGrid>
              <MatchesActions>
                <Button $primary to="/register-player">{t('recentMatches.button')}</Button>
              </MatchesActions>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '2rem 0' }}>
              <div style={{ 
                backgroundColor: '#fef3c7', 
                border: '1px solid #fbbf24', 
                borderRadius: '8px', 
                padding: '1.5rem',
                marginBottom: '1rem'
              }}>
                <p style={{ color: '#92400e', margin: 0, fontSize: '1rem', fontWeight: '500' }}>
                  {seasonHighlights.seasonInfo?.name
                    ? t('recentMatches.emptyTitle', { season: seasonHighlights.seasonInfo.name })
                    : t('recentMatches.emptyTitleGeneric')}
                </p>
                <p style={{ color: '#78350f', margin: '0.5rem 0 0 0', fontSize: '0.875rem' }}>
                  {t('recentMatches.emptyBody')}
                </p>
              </div>
              <MatchesActions style={{ marginTop: '1rem' }}>
                <Button $primary to="/register-player">{t('recentMatches.button')}</Button>
              </MatchesActions>
            </div>
          )}
        </Container>
      </RecentMatchesSection>

      {/* Partner Clubs */}
      <PartnerClubsSection id="clubs">
        <Container>
          <PartnerClubsContent>
            <h2>{t('partnerClubs.title')}</h2>
            <p>{t('partnerClubs.description')}</p>
          </PartnerClubsContent>
        </Container>
      </PartnerClubsSection>

      {/* Call to Action */}
      <CTASection>
        <Container>
          <h2>{t('cta.title')}</h2>
          <p>{t('cta.description')}</p>
          {!embedded && (
          <CTAButtons>
            <Button $secondary $large to="/register-player">{t('cta.buttons.register')}</Button>
            <Button $large to="/login">{t('cta.buttons.login')}</Button>
            <Button $large to="/guide-to-hps">{t('cta.buttons.howLeagueWorks')}</Button>
          </CTAButtons>
          )}
          <CTAStats>
            <CTAStat>
              <span className="cta-number">1</span>
              <span className="cta-label">{t('cta.stats.country')}</span>
            </CTAStat>
            <CTAStat>
              <span className="cta-number">26</span>
              <span className="cta-label">{t('cta.stats.cantons')}</span>
            </CTAStat>
            <CTAStat>
              <span className="cta-number">3</span>
              <span className="cta-label">{t('cta.stats.seasons')}</span>
            </CTAStat>
            <CTAStat>
              <span className="cta-number">18</span>
              <span className="cta-label">{t('cta.stats.games')}</span>
            </CTAStat>
            <CTAStat>
              <span className="cta-number">1</span>
              <span className="cta-label">{t('cta.stats.champion')}</span>
            </CTAStat>
          </CTAStats>
        </Container>
      </CTASection>

      {/* Contact Section */}
      <ContactSection id="contact">
        <Container>
          <h2>{t('contact.title')}</h2>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <ContactForm>
              <form onSubmit={handleContactSubmit}>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="name" 
                    placeholder={t('contact.form.name')} 
                    value={contactForm.name}
                    onChange={handleContactChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="email" 
                    name="email" 
                    placeholder={t('contact.form.email')} 
                    value={contactForm.email}
                    onChange={handleContactChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="subject" 
                    placeholder={t('contact.form.subject')} 
                    value={contactForm.subject}
                    onChange={handleContactChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <textarea 
                    name="message" 
                    placeholder={t('contact.form.message')} 
                    rows="4" 
                    value={contactForm.message}
                    onChange={handleContactChange}
                    required
                  ></textarea>
                </div>
                {submitMessage && (
                  <div style={{ 
                    marginBottom: '16px', 
                    padding: '12px', 
                    borderRadius: '8px',
                    backgroundColor: submitMessage.includes('Thank you') ? '#d1fae5' : '#fee2e2',
                    color: submitMessage.includes('Thank you') ? '#065f46' : '#991b1b',
                    fontSize: '14px'
                  }}>
                    {submitMessage}
                  </div>
                )}
                <SubmitButton type="submit" disabled={isSubmitting}>
                  {isSubmitting ? t('contact.form.submitting') : t('contact.form.submit')}
                </SubmitButton>
              </form>
            </ContactForm>
          </div>
        </Container>
      </ContactSection>
      </HomeContainer>
    </ScrollMemoryWrapper>
  );
};

export default HomePage;